import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DataGridOverlay } from 'utils/component-utils';
import {
  ControlledField,
  CustomPagination,
  PageLoader,
  TextareaInputField,
  colors,
} from 'remote/global';
import { Button, FormHelperText, Grid, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/system';
import {
  useOperatorsUserMessagingUsersQuery,
  usePostOperatorsUserMessagingGroupsMutation,
} from 'api/queries/operators';
import { NewMessageGroupSchemaType, newMessageGroupSchema } from './types';

function CreateNewGroupForm({ onClose }: { onClose: () => void }) {
  const [page, setPage] = useState(0);
  const { data: messageGroupUsers, isSuccess: isSuccessMessageGroupUsers } =
    useOperatorsUserMessagingUsersQuery({
      pageSize: '5',
      pageNo: String(page),
    });
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { isSubmitting, isValidating, isLoading, errors },
  } = useForm<NewMessageGroupSchemaType>({
    defaultValues: {
      description: '',
      members: [],
      name: '',
    },
    resolver: zodResolver(newMessageGroupSchema),
  });
  const postOperatorsUserMessagingGroupsMutation = usePostOperatorsUserMessagingGroupsMutation();

  const members = watch('members');

  const onValid = async (data: NewMessageGroupSchemaType) => {
    await postOperatorsUserMessagingGroupsMutation.mutateAsync(data);
    // Resets the form
    setPage(0);
    reset();
    onClose();
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('MessagesView.group.current.table.header.name') as string,
      flex: 2,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Typography
              sx={{
                color: colors.gray2,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {row.contactInfo.firstName} {row.contactInfo.lastName}
            </Typography>
          </Box>
        );
      },
    },
  ];

  if (!isSuccessMessageGroupUsers) return null;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <DataGrid
            rows={messageGroupUsers?.elements || []}
            getRowId={(row) => row.userId}
            columns={columns}
            rowSelectionModel={members}
            checkboxSelection
            disableColumnSelector
            disableColumnMenu
            pagination
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            autoHeight
            columnHeaderHeight={52}
            paginationModel={{
              page,
              pageSize: 5,
            }}
            sx={{
              ...(errors.members && {
                '.MuiDataGrid-main': {
                  borderColor: colors.busyRed,
                },
              }),
            }}
            onRowSelectionModelChange={(newSelection) => {
              setValue('members', newSelection as any);
            }}
            slots={{
              noRowsOverlay: () => DataGridOverlay(t('Placeholder.noRows')),
              noResultsOverlay: () => DataGridOverlay(t('Placeholder.noResults')),
              loadingOverlay: PageLoader,
              pagination: (props) =>
                CustomPagination({
                  ...props,
                  t,
                  pageCount: Math.ceil(messageGroupUsers.elements.length / 5),
                  pageNo: page,
                  onChange: (_, pageNo) => {
                    setPage(pageNo - 1);
                  },
                }),
            }}
          />
          {errors.members && (
            <FormHelperText sx={{ color: colors.busyRed }}>{errors.members.message}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledField control={control} name='name' translationKey='groupName' />
          <ControlledField
            control={control}
            name='description'
            render={(renderProps) => (
              <TextareaInputField
                {...renderProps}
                translationKey='groupDescription'
                fieldProps={{ minRows: 4 }}
              />
            )}
          />
          <Button
            sx={{ mt: '1rem' }}
            fullWidth
            type='submit'
            variant='contained'
            disabled={isSubmitting || isValidating || isLoading}
          >
            {t('MessagesView.group.actions.createGroup')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default CreateNewGroupForm;
